/**
 * Time utility functions for IST timezone formatting
 */

/**
 * Format ISO timestamp to IST with simple AM/PM format
 * Example: "9:30 AM" or "2:45 PM"
 * @param {string} isoString - ISO 8601 timestamp string
 * @returns {string} Formatted time string
 */
export function formatTimeIST(isoString) {
  if (!isoString) return 'N/A';
  
  try {
    const date = new Date(isoString);
    
    // Format to IST timezone with 12-hour format
    const options = {
      timeZone: 'Asia/Kolkata',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    };
    
    return date.toLocaleString('en-IN', options);
  } catch (error) {
    console.error('Error formatting time:', error);
    return 'Invalid time';
  }
}

/**
 * Format ISO timestamp to IST with date and time
 * Example: "15 Dec 2024, 9:30 AM"
 * @param {string} isoString - ISO 8601 timestamp string
 * @returns {string} Formatted datetime string
 */
export function formatDateTimeIST(isoString) {
  if (!isoString) return 'N/A';
  
  try {
    const date = new Date(isoString);
    
    const options = {
      timeZone: 'Asia/Kolkata',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    };
    
    return date.toLocaleString('en-IN', options);
  } catch (error) {
    console.error('Error formatting datetime:', error);
    return 'Invalid datetime';
  }
}

/**
 * Format time range from start to end
 * Example: "9:30 AM to 10:30 AM"
 * @param {string} startISO - Start time ISO string
 * @param {string} endISO - End time ISO string
 * @returns {string} Formatted time range
 */
export function formatTimeRange(startISO, endISO) {
  const startTime = formatTimeIST(startISO);
  const endTime = formatTimeIST(endISO);
  return `${startTime} to ${endTime}`;
}

/**
 * Get current time in IST
 * @returns {Date} Current date/time in IST
 */
export function getCurrentIST() {
  return new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
}
